import { PDFDocument } from 'pdf-lib';

export const COMPOSER_FILE_ACCEPT = '.pdf,.md,.markdown,.txt,.png,.jpg,.jpeg,.gif,.webp,.mp4,.mov,.webm,.mp3,.wav,.m4a';
export const COMPOSER_DOCUMENT_EXTENSIONS = new Set(['pdf', 'md', 'markdown', 'txt']);
export const COMPOSER_MEDIA_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'mp4', 'mov', 'webm', 'mp3', 'wav', 'm4a']);
export const COMPOSER_SUPPORTED_EXTENSIONS = new Set([...COMPOSER_DOCUMENT_EXTENSIONS, ...COMPOSER_MEDIA_EXTENSIONS]);
export const COMPOSER_MAX_PDF_PAGES = 200;

export function composerFileExtension(file) {
  const name = String(file?.name || '');
  const separator = name.lastIndexOf('.');
  return separator >= 0 ? name.slice(separator + 1).toLowerCase() : '';
}

export function composerFileCategory(file) {
  const extension = composerFileExtension(file);
  if (COMPOSER_DOCUMENT_EXTENSIONS.has(extension)) return 'document';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension)) return 'image';
  if (['mp4', 'mov', 'webm'].includes(extension)) return 'video';
  if (['mp3', 'wav', 'm4a'].includes(extension)) return 'audio';
  return 'unsupported';
}

export async function readPdfPageCount(file) {
  try {
    const pdf = await PDFDocument.load(await file.arrayBuffer());
    return pdf.getPageCount();
  } catch {
    throw new Error(`“${file.name}” could not be read. Choose a valid PDF.`);
  }
}

/**
 * Validate additions independently so an invalid file never clears an existing
 * selection or prevents unrelated valid media in the same picker action.
 */
export async function validateComposerFiles(files, existingFiles = [], { getPdfPageCount = readPdfPageCount } = {}) {
  const accepted = [];
  const errors = [];
  let hasDocument = existingFiles.some((file) => composerFileCategory(file) === 'document');

  for (const file of Array.from(files || [])) {
    const category = composerFileCategory(file);
    if (category === 'unsupported') {
      errors.push(`“${file.name}” isn’t supported. Add an image, video, audio, PDF, Markdown, or TXT file.`);
      continue;
    }
    if (category === 'document' && hasDocument) {
      errors.push('Add one PDF, Markdown, or TXT file at a time. You can still add multiple media files.');
      continue;
    }
    if (composerFileExtension(file) === 'pdf') {
      try {
        const pages = await getPdfPageCount(file);
        if (pages > COMPOSER_MAX_PDF_PAGES) {
          errors.push(`This PDF has ${pages} pages. The maximum is ${COMPOSER_MAX_PDF_PAGES}.`);
          continue;
        }
      } catch (error) {
        errors.push(`“${file.name}” could not be read. Choose a valid PDF.`);
        continue;
      }
    }
    accepted.push(file);
    if (category === 'document') hasDocument = true;
  }

  return { accepted, errors };
}

export function composerAttachmentChip(file, id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`) {
  return {
    id,
    name: file.name,
    size: file.size,
    type: file.type,
    extension: composerFileExtension(file).toUpperCase(),
    file,
  };
}

export function formatComposerFileSize(bytes = 0) {
  if (!bytes) return '0 Bytes';
  const units = ['Bytes', 'KB', 'MB', 'GB'];
  const index = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${Math.round((bytes / (1024 ** index)) * 100) / 100} ${units[index]}`;
}
